import { inject, Injectable } from '@angular/core';
import { createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';
import * as ObservationActions from './observation.actions';
import { Actions } from '@ngrx/effects';
import { Observation, PaginatedResponse } from '../../models/observation';
import { ObservationService } from '../observation.service';
import { Router } from '@angular/router';
import { MediaService } from '../media.service';

@Injectable()
export class ObservationEffects {
  private actions$ = inject(Actions);
  private observationService = inject(ObservationService);
  private mediaService = inject(MediaService);
  private router = inject(Router);

  fetchObservations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ObservationActions.fetchObservations),
      switchMap((action) =>
        this.observationService
          .getObservations({
            assignment: action.assignment,
            creator: action.creator,
            status: action.status,
            living_thing: action.living_thing,
            event: action.event,
            equipment: action.equipment,
            recording_session: action.recording_session,
            page: action.page,
            page_size: action.page_size,
            ordering: action.ordering,
          })
          .pipe(
            map((response: PaginatedResponse<Observation>) =>
              ObservationActions.fetchObservationsSuccess({
                observations: response.results,
                count: response.count,
              })
            ),
            catchError((error) => of(ObservationActions.fetchObservationsFail({ error })))
          )
      )
    )
  );

  fetchObservation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ObservationActions.fetchObservation),
      switchMap((action) =>
        this.observationService.getObservation(action.payload).pipe(
          map((observation: Observation | any) => ObservationActions.fetchObservationSuccess({ observation })),
          catchError((error) => of(ObservationActions.fetchObservationFail({ error })))
        )
      )
    )
  );

  storeObservation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ObservationActions.addObservation),
      switchMap((action) =>
        this.observationService.addObservation(action.payload).pipe(
          map((newObservation: Observation) =>
            ObservationActions.addObservationSuccess({ observation: newObservation })
          ),
          catchError((error) => of(ObservationActions.addObservationFail({ error })))
        )
      )
    )
  );

  updateObservation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ObservationActions.updateObservation),
      switchMap(({ payload }) =>
        this.observationService.updateObservation(payload.id, payload.newObservation).pipe(
          map((updatedObservation: Observation) =>
            ObservationActions.updateObservationSuccess({
              payload: { id: payload.id, newObservation: updatedObservation },
            })
          ),
          catchError((error) => of(ObservationActions.updateObservationFail({ error })))
        )
      )
    )
  );

  redirectToViewPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ObservationActions.addObservationSuccess, ObservationActions.updateObservationSuccess),
        map((action) => {
          if ('observation' in action) {
            return action.observation.id;
          } else {
            return action.payload.id;
          }
        }),
        map((id) => this.router.navigate(['/observations', id]))
      ),
    { dispatch: false }
  );

  deleteObservation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ObservationActions.deleteObservation),
      switchMap((action) =>
        this.observationService.deleteObservation(action.payload).pipe(
          map(() => ObservationActions.deleteObservationSuccess({ payload: action.payload })),
          catchError((error) => of(ObservationActions.deleteObservationFail({ error })))
        )
      )
    )
  );

  redirectAfterDelete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ObservationActions.deleteObservationSuccess),
        map(() => this.router.navigate(['/observations']))
      ),
    { dispatch: false }
  );

  startAddObservationWithMedia$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ObservationActions.startAddObservationWithMedia),
      switchMap(({ observation, audioFiles, imageFiles, videoFiles }) =>
        this.observationService.addObservation(observation).pipe(
          switchMap((createdObs) =>
            this.mediaService.uploadAllMedia(createdObs.id!, audioFiles, imageFiles, videoFiles).pipe(
              switchMap(() =>
                this.observationService.getObservation(createdObs.id!).pipe(
                  // 👈 fetch full updated observation
                  map((fullObs) => {
                    if (!fullObs) {
                      throw new Error('Observation not found');
                    }
                    return ObservationActions.addObservationSuccess({ observation: fullObs });
                  })
                )
              )
            )
          ),
          catchError((error) => of(ObservationActions.addObservationFail({ error })))
        )
      )
    )
  );

  startUpdateObservationWithMedia$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ObservationActions.startUpdateObservationWithMedia),
      switchMap(({ payload }) => {
        return this.mediaService.uploadAllMedia(payload.id, payload.audioFiles, payload.imageFiles, payload.videoFiles).pipe(
          switchMap(() => {
            return this.observationService.updateObservation(payload.id, payload.newObservation).pipe(
              map((finalObservation) =>
                ObservationActions.updateObservationSuccess({
                  payload: { id: payload.id, newObservation: finalObservation },
                })
              )
            );
          })
        );
      })
    )
  );
}
